import { useMemo, useState } from 'react'
import { Edit16Filled } from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import 'katex/dist/katex.min.css'
import styles from './Answer.module.css'
import { ChatAppResponse, getCitationFilePath } from '../../api'
import { parseAnswerToHtml } from './AnswerParser'

// Citations collapse to this many chips by default; the rest reveal on expand.
const MAX_VISIBLE_CITATIONS = 6

interface Props {
    answer: ChatAppResponse
    isSelected?: boolean
    isStreaming: boolean
    onCitationClicked: (filePath: string) => void
    onFeedbackClicked: () => void
    onFollowupQuestionClicked?: (question: string) => void
    showFollowupQuestions?: boolean
}

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onFeedbackClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
}: Props) => {
    const [showAllCitations, setShowAllCitations] = useState(false)
    const followupQuestions = answer.context?.followup_questions
    const messageContent = answer.message.content
    const citationLookup = answer.context?.data_points?.citation_lookup || {}
    const parsedAnswer = useMemo(
        () =>
            parseAnswerToHtml(
                messageContent,
                isStreaming,
                styles.supContainer,
                citationLookup
            ),
        [answer]
    )
    // Markdown produces headings/paragraphs/lists/tables; KaTeX produces styled
    // spans; citations produce buttons. Links and images are stripped (their
    // text is kept) so model output cannot inject navigation or remote content.
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ['style', 'img', 'a'],
        ADD_ATTR: ['data-citation-path'],
    })

    const handleInlineCitationClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const trigger = (e.target as HTMLElement).closest<HTMLElement>(
            '[data-citation-path]'
        )
        if (!trigger) return
        const path = trigger.getAttribute('data-citation-path')
        if (path) onCitationClicked(path)
    }

    return (
        <div
            className={`${styles.answerContainer} ${isSelected && styles.selected}`}
        >
            <div>
                <div
                    className={styles.answerText}
                    onClick={handleInlineCitationClick}
                    dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}
                ></div>
            </div>
            {!!parsedAnswer.citations.length && (
                <div>
                    <div>
                        <span className={styles.citationLearnMore}>
                            Citations
                        </span>
                        <button
                            style={{ color: 'var(--color-bg-brand)' }}
                            title="Provide feedback on suggestion"
                            aria-label="Provide feedback on suggestion"
                            onClick={() => onFeedbackClicked()}
                            disabled={!answer.context.data_points}
                            className={styles.editButton}
                        >
                            <Edit16Filled />
                        </button>
                    </div>
                    <div className={styles.citationContainer}>
                        {(showAllCitations
                            ? parsedAnswer.citations
                            : parsedAnswer.citations.slice(
                                  0,
                                  MAX_VISIBLE_CITATIONS
                              )
                        ).map((x, i) => {
                            // Use citation lookup metadata if available, otherwise use the citation directly
                            const metadata = citationLookup[x]
                            const displayText = metadata?.display || x
                            const path = metadata?.path
                                ? getCitationFilePath(metadata.path)
                                : getCitationFilePath(x)
                            const page = metadata?.pageOrSection

                            return (
                                <a
                                    key={i}
                                    className={styles.citation}
                                    title={
                                        page
                                            ? `${displayText} (${page})`
                                            : displayText
                                    }
                                    onClick={() => onCitationClicked(path)}
                                >
                                    <span className={styles.citationNumber}>
                                        {i + 1}
                                    </span>
                                    <span className={styles.citationTitle}>
                                        {displayText}
                                    </span>
                                </a>
                            )
                        })}
                        {parsedAnswer.citations.length >
                            MAX_VISIBLE_CITATIONS && (
                            <button
                                type="button"
                                className={styles.citationToggle}
                                onClick={() =>
                                    setShowAllCitations((prev) => !prev)
                                }
                            >
                                {showAllCitations
                                    ? 'Show less'
                                    : `+${parsedAnswer.citations.length - MAX_VISIBLE_CITATIONS} more`}
                            </button>
                        )}
                    </div>
                </div>
            )}
            {!!followupQuestions?.length &&
                showFollowupQuestions &&
                onFollowupQuestionClicked && (
                    <div>
                        <div
                            className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ''}`}
                        >
                            <span className={styles.followupQuestionLearnMore}>
                                Follow-up questions:
                            </span>
                            {followupQuestions.map((x, i) => {
                                return (
                                    <a
                                        key={i}
                                        className={styles.followupQuestion}
                                        title={x}
                                        onClick={() =>
                                            onFollowupQuestionClicked(x)
                                        }
                                    >
                                        {`${x}`}
                                    </a>
                                )
                            })}
                        </div>
                    </div>
                )}
        </div>
    )
}
